import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPageVisibility } from '../api/generalApi';
import { 
  HomeIcon, 
  CogIcon, 
  UserGroupIcon,
  TagIcon,
  CalendarIcon,
  ScaleIcon,
  ChevronDownIcon,
  UsersIcon,
  TableCellsIcon,
  ChartPieIcon,
  RectangleGroupIcon,
  XMarkIcon,
  ChartBarIcon,
  ListBulletIcon,
  TrophyIcon,
  InformationCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

// Icon component type from heroicons
type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

// Interface for navigation items
interface NavItem {
  name: string;
  href?: string;
  icon: IconType;
  adminOnly: boolean;
  subItems?: SubNavItem[];
  isOpen?: boolean;
  setOpen?: () => void;
}

// Interface for sub-navigation items
interface SubNavItem {
  name: string;
  href: string;
  icon?: IconType;
}

// Add props for open and setOpen
interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [configOpen, setConfigOpen] = useState<boolean>(false);
  const [betsOpen, setBetsOpen] = useState<boolean>(false);
  const [infoOpen, setInfoOpen] = useState<boolean>(false);

  const isAdmin = user?.is_admin || false;

  const [pageVisibility, setPageVisibility] = useState<{ [key: string]: boolean }>({
    tournament_stats: true,
    general_bets_summary: true,
    fun_facts: true,
    side_bets: true,
  });

  useEffect(() => {
    if (!user) return;
    getPageVisibility()
      .then(setPageVisibility)
      .catch(() => {});
  }, [user]);

  const navItems: NavItem[] = [
    { name: 'בית', href: '/dashboard', icon: HomeIcon, adminOnly: false },
    { name: 'טבלת ניקוד', href: '/leaderboard', icon: TrophyIcon, adminOnly: false },
    { name: 'כל המשחקים', href: '/bets/matches', icon: ListBulletIcon, adminOnly: false },
    { name: 'בתים', href: '/bets/groups', icon: TableCellsIcon, adminOnly: false },
    { name: 'כלליים', href: '/bets/general', icon: RectangleGroupIcon, adminOnly: false},
    ...(isAdmin || pageVisibility.general_bets_summary ? [{ name: 'סיכום כלליים', href: '/tournament-stats/general-bets-summary', icon: ChartPieIcon, adminOnly: false }] : []),
    ...(isAdmin || pageVisibility.tournament_stats ? [{ name: 'סטטיסטיקות', href: '/tournament-stats', icon: ChartBarIcon, adminOnly: false }] : []),
    ...(isAdmin || pageVisibility.fun_facts ? [{ name: 'Fun Facts', href: '/fun-facts', icon: InformationCircleIcon, adminOnly: false }] : []),
    ...(isAdmin || pageVisibility.side_bets ? [{ name: 'בית צדדי', href: '/side-bets', icon: UsersIcon, adminOnly: false }] : []),
    { name: 'צור קשר', href: '/contact', icon: EnvelopeIcon, adminOnly: false }
    // { 
    //   name: 'ההימורים שלי',
    //   icon: TicketIcon,
    //   adminOnly: false,
    //   subItems: [
    //     { name: 'כל המשחקים', href: '/bets/matches', icon: ClipboardDocumentListIcon },
    //     { name: 'הימורים כלליים', href: '/bets/general', icon: TagIcon },
    //     { name: 'הימורי בתים', href: '/bets/groups', icon: UserGroupIcon },
    //     { name: 'נוק אאוט', href: '/bets/knockout', icon: TrophyIcon },
    //   ],
    //   isOpen: betsOpen,
    //   setOpen: () => setBetsOpen(!betsOpen)
    // },
    // { 
    //   name: 'מידע',
    //   icon: InformationCircleIcon,
    //   adminOnly: false,
    //   subItems: [
    //     { name: 'סטטיסטיקות של הטורניר', href: '/tournament-stats', icon: ChartBarIcon },
    //     { name: 'סיכום הימורים כלליים', href: '/tournament-stats/general-bets-summary', icon: ChartBarIcon },
    //     { name: 'הימורים כלליים של כולם', href: '/tournament-stats/users-general-bets', icon: ChartBarIcon },
    //   ],
    //   isOpen: infoOpen,
    //   setOpen: () => setInfoOpen(!infoOpen)
    // },
  ];

  const adminNavItems: NavItem[] = [
    { 
      name: 'הגדרות', 
      icon: CogIcon, 
      adminOnly: true,
      subItems: [
        { name: 'Teams', href: '/admin/teams', icon: UserGroupIcon },
        { name: 'Groups', href: '/admin/groups', icon: TagIcon },
        { name: 'Matches', href: '/admin/matches', icon: CalendarIcon },
        { name: 'Users', href: '/admin/users', icon: UsersIcon },
        { name: 'Points', href: '/admin/points', icon: ScaleIcon },
        { name: 'General', href: '/admin/general', icon: ScaleIcon }
      ],
      isOpen: configOpen,
      setOpen: () => setConfigOpen(!configOpen)
    }
  ];

  // Combine regular and admin items based on user role
  const allNavItems: NavItem[] = [
    ...navItems,
    ...(isAdmin ? adminNavItems : [])
  ];

  // Sidebar classes for mobile/desktop
  const sidebarBase =
    'flex flex-col h-full bg-stake-sidebar text-gray-300 w-64 fixed right-0 border-l border-stake-border z-40 transition-transform duration-300';
  const sidebarMobile = open
    ? 'translate-x-0'
    : 'translate-x-full';
  const sidebarDesktop = 'md:translate-x-0';

  // Close sidebar on mobile when clicking a link
  const handleLinkClick = () => {
    setOpen(false);
  };

  return (
    <div
      className={`${sidebarBase} ${sidebarMobile} ${sidebarDesktop}`}
      style={{ top: 0, bottom: 0 }}
      aria-label="Sidebar"
    >
      {/* Close button for mobile */}
      <div className="flex items-center justify-between h-16 border-b border-stake-border px-4 md:justify-center md:px-0">
        <h2 className="text-xl font-bold text-stake-blue">תפריט</h2>
        <button
          className="md:hidden p-2 rounded hover:bg-dark-200 focus:outline-none focus:ring-2 focus:ring-stake-blue"
          onClick={() => setOpen(false)}
          aria-label="Close sidebar"
        >
          <XMarkIcon className="h-7 w-7 text-white" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-6 px-2">
        <ul className="space-y-2">
          {allNavItems.map((item) => {
            // Skip admin-only items for non-admin users
            if (item.adminOnly && !isAdmin) return null;

            if (item.subItems) {
              // This is a dropdown menu item
              return (
                <li key={item.name} className="mb-2">
                  <button
                    onClick={item.setOpen}
                    className="flex items-center w-full px-4 py-3 rounded-md text-gray-300 hover:bg-dark-100 transition-colors duration-150"
                  >
                    <item.icon className="h-5 w-5 ml-3 text-gray-400" />
                    <span className="font-medium">{item.name}</span>
                    <ChevronDownIcon 
                      className={`h-4 w-4 mr-auto transition-transform duration-200 ${item.isOpen ? 'transform rotate-180' : ''}`} 
                    />
                  </button>
                  {item.isOpen && (
                    <ul className="mt-2 mr-4 space-y-1">
                      {item.subItems.map((subItem) => {
                        const isActive = location.pathname === subItem.href;
                        return (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.href}
                              onClick={handleLinkClick}
                              className={`flex items-center px-4 py-2 rounded-md text-sm transition-colors duration-150 ${
                                isActive
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-300 hover:bg-dark-100'
                              }`}
                            >
                              {subItem.icon && (
                                <subItem.icon className={`h-4 w-4 ml-3 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              )}
                              <span>{subItem.name}</span>
                              {isActive && (
                                <div className="w-1.5 h-1.5 mr-auto rounded-full bg-stake-blue"></div>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            } else {
              // This is a regular menu item
              const isActive = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href || ''}
                    onClick={handleLinkClick}
                    className={`flex items-center px-4 py-3 rounded-md transition-colors duration-150 ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-300 hover:bg-dark-100'
                    }`}
                  >
                    <item.icon className={`h-5 w-5 ml-3 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                    <span className="font-medium">{item.name}</span>
                    {isActive && (
                      <div className="w-1.5 h-1.5 mr-auto rounded-full bg-stake-blue"></div>
                    )}
                  </Link>
                </li>
              );
            }
          })}
        </ul>
      </nav>
      <div className="p-4 border-t border-stake-border">
        <Link to="/profile" onClick={handleLinkClick} className="flex items-center hover:bg-dark-100 rounded-md p-1 transition-colors duration-150">
          <div className="ml-3 bg-stake-blue rounded-full h-9 w-9 flex items-center justify-center shadow-md">
            <span className="text-white font-medium">
              {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
            </span>
          </div>
          <div>
            <p className="text-white font-medium">{user?.first_name} {user?.last_name}</p>
            {isAdmin && <p className="text-gray-400 text-xs">Administrator</p>}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Sidebar; 